import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Tabs,
  Tab,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person } from '@mui/icons-material';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';
import { 
  signInWithPopup, 
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  Auth 
} from 'firebase/auth';
import logger from '../utils/logger';

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  auth: Auth;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const AuthDialog: React.FC<AuthDialogProps> = ({ open, onClose, auth }) => {
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    setError('');
    setSuccess('');
  };

  const handleGoogleSignIn = async () => {
    logger.log('🔐 Google Sign-In button clicked - trying popup method first');
    try {
      setGoogleLoading(true);
      setError('');
      logger.log('🔐 Creating GoogleAuthProvider...');
      const provider = new GoogleAuthProvider();
      
      try {
        // Single domain architecture - use popup auth consistently
        logger.log('🔐 Attempting popup sign-in...');
        const result = await signInWithPopup(auth, provider);
        logger.log('✅ Popup sign-in successful:', result.user.email);
        onClose(); // Close the dialog on success
      } catch (popupError: any) {
        logger.log('❌ Popup failed:');
        logger.log('❌ Error code:', popupError.code);
        logger.log('❌ Error message:', popupError.message);
        logger.log('❌ Full error:', popupError);
        
        // Check for COOP or popup issues and fall back to redirect
        const shouldFallback = popupError.code === 'auth/popup-blocked' || 
                              popupError.code === 'auth/popup-closed-by-user' ||
                              popupError.message?.includes('Cross-Origin-Opener-Policy') ||
                              popupError.message?.includes('window.closed');
        
        if (shouldFallback) {
          logger.log('🔄 Popup blocked by COOP policy, falling back to redirect method...');
          await signInWithRedirect(auth, provider);
          logger.log('🔐 Redirect initiated - page will redirect to Google...');
        } else {
          throw popupError; // Re-throw other errors
        }
      }
    } catch (error: any) {
      logger.error('🔐 Google sign in failed:', error);
      logger.error('🔐 Error code:', error.code);
      logger.error('🔐 Error message:', error.message);
      setError(`Failed to sign in with Google: ${error.message}`);
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    logger.log('🍎 Apple Sign-In button clicked - trying popup method first');
    try {
      setAppleLoading(true);
      setError('');
      logger.log('🍎 Creating Apple OAuth provider...');
      const provider = new OAuthProvider('apple.com');
      
      // Request additional scopes
      provider.addScope('email');
      provider.addScope('name');
      
      try {
        // Single domain architecture - use popup auth consistently
        logger.log('🍎 Attempting popup sign-in...');
        const result = await signInWithPopup(auth, provider);
        logger.log('✅ Apple popup sign-in successful:', result.user.email);
        onClose(); // Close the dialog on success
      } catch (popupError: any) {
        logger.log('❌ Apple popup failed:');
        logger.log('❌ Error code:', popupError.code);
        logger.log('❌ Error message:', popupError.message);
        logger.log('❌ Full error:', popupError);
        
        // Check for COOP or popup issues and fall back to redirect
        const shouldFallback = popupError.code === 'auth/popup-blocked' || 
                              popupError.code === 'auth/popup-closed-by-user' ||
                              popupError.message?.includes('Cross-Origin-Opener-Policy') ||
                              popupError.message?.includes('window.closed');
        
        if (shouldFallback) {
          logger.log('🔄 Apple popup blocked by COOP policy, falling back to redirect method...');
          await signInWithRedirect(auth, provider);
          logger.log('🍎 Redirect initiated - page will redirect to Apple...');
        } else {
          throw popupError; // Re-throw other errors
        }
      }
    } catch (error: any) {
      logger.error('🍎 Apple sign in failed:', error);
      logger.error('🍎 Error code:', error.code);
      logger.error('🍎 Error message:', error.message);
      setError(`Failed to sign in with Apple: ${error.message}`);
      setAppleLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    try {
      setEmailLoading(true);
      setError('');
      await signInWithEmailAndPassword(auth, email, password);
      onClose();
    } catch (error: any) {
      logger.error('Email sign in failed:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError('Failed to sign in. Please check your credentials.');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    try {
      setEmailLoading(true);
      setError('');
      await createUserWithEmailAndPassword(auth, email, password);
      onClose();
    } catch (error: any) {
      logger.error('Email sign up failed:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setEmailLoading(true);
      setError('');
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      logger.error('Password reset failed:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // Helper variable for any loading state
  const anyLoading = emailLoading || googleLoading || appleLoading;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box
            component="img"
            src="/jugglevision-logo-nobg-dark.svg"
            srcSet="/jugglevision-logo-nobg-dark.svg 1x, /jugglevision-logo-nobg-dark.svg 2x"
            sizes="48px"
            alt="JuggleVision"
            loading="lazy"
            sx={{ height: 48, width: 'auto', mb: 1 }}
          />
          <Typography variant="h6" component="div" color="text.secondary">
            Access
          </Typography>
        </Box>
        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
          <Tab label="Sign In" />
          <Tab label="Sign Up" />
          <Tab label="Reset Password" />
        </Tabs>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Sign In Tab */}
        <TabPanel value={tab} index={0}>
          <Box component="form" onSubmit={handleEmailSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={emailLoading}
              sx={{ mt: 1 }}
            >
              {emailLoading ? 'Signing In...' : 'Sign In with Email'}
            </Button>
            
            <Typography variant="body2" textAlign="center" sx={{ my: 1 }}>
              or
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <GoogleSignInButton
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                loading={googleLoading}
                text="Sign in with Google"
                variant="dark"
              />
              
              <AppleSignInButton
                onClick={handleAppleSignIn}
                disabled={appleLoading}
                loading={appleLoading}
                text="Sign in with Apple"
                variant="black"
              />
            </Box>
          </Box>
        </TabPanel>

        {/* Sign Up Tab */}
        <TabPanel value={tab} index={1}>
          <Box component="form" onSubmit={handleEmailSignUp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Password must be at least 6 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={emailLoading}
              sx={{ mt: 1 }}
            >
              {emailLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
            
            <Typography variant="body2" textAlign="center" sx={{ my: 1 }}>
              or
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <GoogleSignInButton
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                loading={googleLoading}
                text="Sign up with Google"
                variant="dark"
              />
              
              <AppleSignInButton
                onClick={handleAppleSignIn}
                disabled={appleLoading}
                loading={appleLoading}
                text="Sign up with Apple"
                variant="black"
              />
            </Box>
          </Box>
        </TabPanel>

        {/* Reset Password Tab */}
        <TabPanel value={tab} index={2}>
          <Box component="form" onSubmit={handlePasswordReset} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={emailLoading}
              sx={{ mt: 1 }}
            >
              {emailLoading ? 'Sending...' : 'Send Reset Email'}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={anyLoading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};